// Billing: the organization's plan and usage, the plans side by side, and (for owners and admins) upgrading through Stripe
// Checkout or managing payment in Stripe's customer portal. Card details never touch this site.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatSize } from "../../lib/chat";
import { Alert, Badge, Button, Card, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg } from "../org";

type Price = { amount: number; currency: string; interval: string } | null;
type Plan = {
  id: string; name: string; description: string; isDefault: boolean; trialDays: number; buyable: boolean;
  maxMembers: number; maxCallParticipants: number; groupCallMinutes: number | null; storageBytes: number; recording: boolean; transcripts: boolean; maxProjects?: number | null; freeClients?: boolean;
  prices: { monthly: Price; yearly: Price };
};
type Billing = {
  enabled: boolean; canManage: boolean; plan: { id: string; name: string }; source: "free" | "billing" | "override"; overrideUntil: string | null;
  plans: Plan[];
  usage: { members: number; storageBytes: number };
  subscription: { status: string; quantity: number; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null } | null;
};

const money = (p: NonNullable<Price>) => new Intl.NumberFormat(undefined, { style: "currency", currency: p.currency.toUpperCase(), minimumFractionDigits: p.amount % 100 ? 2 : 0 }).format(p.amount / 100);
const day = (iso: string) => new Date(iso).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" });

export function BillingPage() {
  const { org, role } = useOrg();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [interval, setInterval_] = useState<"month" | "year">("month");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const returned = params.get("checkout");
  const data = useQuery({
    queryKey: ["billing", org.slug], queryFn: () => api<Billing>(`/orgs/${org.slug}/billing`), enabled: atLeast(role, "admin"),
    // Back from Stripe: Stripe tells the server in a moment; check every two seconds until it has.
    refetchInterval: (q) => (returned === "success" && q.state.data?.source !== "billing" ? 2000 : false),
  });
  const b = data.data;
  const paid = b?.source === "billing";
  useEffect(() => { if (returned === "success" && paid) void qc.invalidateQueries({ queryKey: ["org", org.slug] }); }, [returned, paid, qc, org.slug]);

  async function go(path: "checkout" | "portal", plan?: string) {
    setBusy(plan ?? path); setError(null);
    try {
      const r = await api<{ url: string }>(`/orgs/${org.slug}/billing/${path}`, { method: "POST", body: path === "checkout" ? { plan, interval } : {} });
      window.location.assign(r.url);
    } catch (err) { setError(errorText(err)); setBusy(null); }
  }

  if (!atLeast(role, "admin")) return <div className="mx-auto max-w-3xl"><Card><p className="text-sm text-slate-600">Only admins and owners can see billing.</p></Card></div>;
  if (!b) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  const current = b.plans.find((p) => p.id === b.plan.id)!;
  const anyYearly = b.plans.some((p) => p.prices.yearly);
  const priceOf = (p: Plan) => (interval === "year" && p.prices.yearly ? p.prices.yearly : p.prices.monthly);

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div><h1 className="text-2xl font-semibold tracking-tight">Billing</h1><p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{org.name}'s plan and usage.</p></div>
      {returned === "success" && (paid
        ? <Alert kind="success">Thank you! {org.name} is on {b.plan.name}.</Alert>
        : <Alert kind="info"><span className="inline-flex items-center gap-2"><Spinner className="h-4 w-4" />Payment received. Confirming your upgrade with Stripe…</span></Alert>)}
      {returned === "cancelled" && <Alert kind="info">Checkout was cancelled; nothing was charged. <button type="button" className="underline" onClick={() => setParams({})}>Dismiss</button></Alert>}
      {error && <Alert>{error}</Alert>}

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500">Current plan</p>
            <p className="mt-1 flex items-center gap-2 text-2xl font-semibold">{b.plan.name}{b.subscription?.status === "past_due" && <Badge tone="red">Payment failed</Badge>}</p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {b.source === "override" ? `Provided by the platform${b.overrideUntil ? ` until ${day(b.overrideUntil)}` : ""}; not billed.`
                : b.source === "billing" && b.subscription ? `${b.subscription.quantity} ${b.subscription.quantity === 1 ? "seat" : "seats"} · ${b.subscription.cancelAtPeriodEnd ? "ends" : "renews"} ${b.subscription.currentPeriodEnd ? `on ${day(b.subscription.currentPeriodEnd)}` : ""}`
                : current.isDefault ? "Free forever. Upgrade any time." : ""}
            </p>
          </div>
          {b.canManage && b.enabled && b.source === "billing" && <Button variant="secondary" onClick={() => go("portal")} loading={busy === "portal"}>Manage billing</Button>}
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <Usage label="People" used={b.usage.members} max={current.maxMembers} text={`${b.usage.members} of ${current.maxMembers}`} />
          <Usage label="File storage" used={b.usage.storageBytes} max={current.storageBytes} text={`${formatSize(b.usage.storageBytes)} of ${formatSize(current.storageBytes)}`} />
        </div>
        {b.subscription?.status === "past_due" && b.canManage && <div className="mt-4"><Alert>The last payment didn't go through. Update the card in Manage billing to keep {b.plan.name}.</Alert></div>}
      </Card>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Plans</h2>
          {anyYearly && (
            <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm dark:bg-slate-800" role="radiogroup" aria-label="Billing period">
              {(["month", "year"] as const).map((i) => (
                <button key={i} type="button" role="radio" aria-checked={interval === i} onClick={() => setInterval_(i)}
                  className={cx("rounded-md px-3 py-1 font-medium", interval === i ? "bg-white shadow-sm dark:bg-slate-900" : "text-slate-600 dark:text-slate-400")}>{i === "month" ? "Monthly" : "Yearly"}</button>
              ))}
            </div>
          )}
        </div>
        <div className={cx("grid gap-4", b.plans.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2")}>
          {b.plans.map((p) => {
            const price = priceOf(p);
            return (
              <Card key={p.id} className={cx("flex flex-col", p.id === b.plan.id && "ring-2 ring-brand-500")}>
                <div className="flex items-center justify-between"><h3 className="text-lg font-semibold">{p.name}</h3>{p.id === b.plan.id && <Badge tone="brand">Current</Badge>}</div>
                {p.description && <p className="mt-1 text-sm text-slate-500">{p.description}</p>}
                <p className="mt-2 flex flex-wrap items-baseline gap-x-1.5 text-3xl font-semibold">
                  {p.isDefault ? <span className="text-xl">No charge</span> : price ? <>{money(price)}<span className="whitespace-nowrap text-base font-normal text-slate-500">per person / {price.interval}</span></> : <span className="text-base font-normal text-slate-500">Price on request</span>}
                </p>
                {!p.isDefault && p.trialDays > 0 && <p className="mt-1 text-sm font-medium text-emerald-700 dark:text-emerald-300">{p.trialDays}-day free trial</p>}
                <ul className="mt-4 flex-1 space-y-2 text-sm">
                  <Feature>Up to {p.maxMembers.toLocaleString()} people</Feature>
                  <Feature>{p.groupCallMinutes ? `Meetings and group calls up to ${p.groupCallMinutes} minutes` : "Meetings and group calls with no time limit"}</Feature>
                  <Feature>Up to {p.maxCallParticipants} people per call or meeting</Feature>
                  <Feature>{formatSize(p.storageBytes)} of file storage</Feature>
                  <Feature>{p.maxProjects ? `Up to ${p.maxProjects} project${p.maxProjects === 1 ? "" : "s"} at a time, with boards and tasks` : "Unlimited projects, boards and tasks"}</Feature>
                  {p.freeClients && <Feature>Invite clients to projects free: they don't count as people</Feature>}
                  {p.recording && <Feature>Record meetings and calls</Feature>}
                  {p.transcripts && <Feature>Write down what is said, and search it</Feature>}
                  <Feature>Unlimited chat, one-to-one calls, guests and scheduling</Feature>
                </ul>
                {!p.isDefault && p.id !== b.plan.id && !paid && (
                  b.canManage
                    ? p.buyable
                      ? <Button className="mt-6 w-full" onClick={() => go("checkout", p.id)} loading={busy === p.id}>{p.trialDays > 0 ? "Start free trial" : `Choose ${p.name}`}</Button>
                      : <p className="mt-6 text-sm text-slate-500">{b.enabled ? `${p.name} can't be bought online yet. Contact us to switch.` : "Online payment isn't set up on this server yet. Contact us to upgrade."}</p>
                    : <p className="mt-6 text-sm text-slate-500">Only owners and admins can change the plan.</p>
                )}
              </Card>
            );
          })}
        </div>
        {b.enabled && <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><Icon name="lock" className="h-3.5 w-3.5" />Payments are handled by Stripe; card details never reach our servers. Seats follow the number of people in the organization.</p>}
      </section>
    </div>
  );
}

function Usage({ label, used, max, text }: { label: string; used: number; max: number; text: string }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, max)) * 100));
  return (
    <div>
      <div className="flex justify-between text-sm"><span className="font-medium">{label}</span><span className="text-slate-500">{text}</span></div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={cx("h-full rounded-full", pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-brand-500")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Feature({ children }: { children: React.ReactNode }) {
  return <li className="flex gap-2"><svg viewBox="0 0 20 20" className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" fill="currentColor" aria-hidden="true"><path d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.6l7.3-7.3a1 1 0 0 1 1.4 0z" /></svg><span>{children}</span></li>;
}
